import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'

interface SEOHeadProps {
  title: string
  description: string
  keywords?: string
  canonicalUrl?: string
  openGraph?: Record<string, string>
  twitterCard?: Record<string, string>
  structuredData?: object | object[]
  noIndex?: boolean
  robots?: string
  alternateUrls?: Array<{ hreflang: string; href: string }>
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  openGraph = {},
  twitterCard = {},
  structuredData,
  noIndex = false,
  robots,
  alternateUrls = []
}) => {
  const { i18n } = useTranslation()
  const currentLang = i18n.language || 'fr'
  const contentLanguage = currentLang === 'ar' ? 'ar-MA' : currentLang === 'en' ? 'en-US' : 'fr-MA'
  
  // Determine robots content: explicit robots prop > noIndex > default
  const robotsContent = robots || (noIndex ? "noindex,nofollow" : "index,follow")
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Robots */}
      <meta name="robots" content={robotsContent} />
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph */}
      {Object.entries(openGraph).map(([property, content]) => (
        <meta key={property} property={property} content={content} />
      ))}
      
      {/* Twitter Card */}
      {Object.entries(twitterCard).map(([name, content]) => (
        <meta key={name} name={name} content={content} />
      ))}
      
      {/* Alternate URLs for different languages */}
      {alternateUrls.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}
      
      {/* Structured Data */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((schema, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(schema)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )
      )}
      
      {/* Additional SEO tags */}
      <meta name="author" content="KiraCar" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Language" content={contentLanguage} />
      <html lang={currentLang} />
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    </Helmet>
  )
}

export default SEOHead
